import { recordingReducer } from "./recording-slice.js";
import { createPersistenceMiddleware } from "./persistence-middleware.js";
import { arElementsReducer, gpsDataReducer, gpsElementsReducer, sanitizeForDevTools, validateLicenseKey } from "gps-plus-slam-js";
import { configureStore } from "@reduxjs/toolkit";
import { COMMUNITY_LICENSE_KEY } from "gps-plus-slam-js/community-license-key";
//#region ../src/state/create-slam-app-store.ts
/**
* `createSlamAppStore` — composable Redux store factory for AR+GPS apps.
*
* Introduced in Iter 1 of the AppFramework/RecorderApp boundary migration.
* Wires the three library reducers (`gpsData`, `gpsElements`, `arElements`),
* the framework's recording lifecycle slice, and the persistence middleware.
*
* Recorder-only state (routing screen, ref-points, scenario) is plugged in
* by the consumer via `extraReducers` / `extraMiddleware`. The factory itself
* never references those concepts so apps that don't need them (POI viewers,
* navigation arrows, …) compose freely.
*
* The legacy `createRecorderStore` from `store.ts` is built on top of this
* factory and will move out of the framework in Iter 1D.
*
* @see docs/2026-05-03-appframework-vs-recorderapp-boundary-analysis.md — Iter 1
*/
/**
* Build a Redux store wired with library + recording slices, persistence
* middleware, and any caller-supplied extras. See module docstring for the
* design rationale.
*/
function createSlamAppStore(options) {
	const { storageBackend, extraReducers, extraMiddleware, onWriteFailure, enableDevChecks = true, licenseKey = COMMUNITY_LICENSE_KEY } = options;
	validateLicenseKey(licenseKey);
	const store = configureStore({
		reducer: {
			gpsData: gpsDataReducer,
			gpsElements: gpsElementsReducer,
			arElements: arElementsReducer,
			recording: recordingReducer,
			...extraReducers ?? {}
		},
		middleware: (getDefaultMiddleware) => getDefaultMiddleware({
			serializableCheck: enableDevChecks,
			immutableCheck: enableDevChecks
		}).concat(createPersistenceMiddleware({
			storageBackend,
			onWriteFailure
		}), ...extraMiddleware ?? []),
		devTools: {
			actionSanitizer: sanitizeForDevTools,
			stateSanitizer: sanitizeForDevTools
		}
	});
	return {
		getState: () => store.getState(),
		dispatch: store.dispatch,
		subscribe: (listener) => store.subscribe(listener),
		writeFrame: (blob, index) => storageBackend.writeFrame(blob, index),
		writeSessionMetadata: (metadata) => storageBackend.writeSessionMetadata(metadata)
	};
}
//#endregion
export { createSlamAppStore };
